import { useState } from "react";
import { Link } from "react-router-dom";

import { hasClerk } from "../auth/clerk";
import LandingPlayLink from "./LandingPlayLink";

const PLAY_LINK_CLASSES =
  "inline-block px-8 py-3 rounded-lg border border-lucid-accent/60 text-lucid-accent font-ui text-lg hover:bg-lucid-elevated hover:border-lucid-accent transition-colors";

const CREATE_LINK_CLASSES =
  "inline-flex items-center gap-2 px-8 py-3 rounded-lg border border-lucid-text/20 text-lucid-text/40 font-ui text-lg cursor-not-allowed select-none";

const TAB_BASE =
  "font-ui text-sm px-3 py-1 rounded-md border transition-colors";
const TAB_ACTIVE =
  "border-lucid-accent text-lucid-accent bg-lucid-elevated";
const TAB_INACTIVE =
  "border-lucid-accent/30 text-lucid-text/60 hover:text-lucid-accent hover:border-lucid-accent/60";

const ENTRY_HEADING =
  "text-lucid-accent/90 font-ui text-xs uppercase tracking-wider mb-1";

export default function Landing() {
  const [tab, setTab] = useState<"new" | "next">("new");

  return (
    <main className="min-h-screen w-full flex items-center justify-center bg-lucid-deep text-lucid-text px-6 py-12">
      <div className="max-w-xl w-full text-center space-y-8">
        <h1 className="font-narration text-5xl md:text-6xl text-lucid-accent tracking-wide">
          Sunday Dungeon
        </h1>
        <p className="font-narration text-lg md:text-xl leading-relaxed text-lucid-text/90">
          A language-based adventure simulator. Type what you want to do, and
          the world responds — anything you can describe in words, you can try.
        </p>
        <p className="font-narration text-lg md:text-xl leading-relaxed text-lucid-text/90">
          Updated every Sunday.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          {hasClerk() ? (
            <LandingPlayLink className={PLAY_LINK_CLASSES} />
          ) : (
            <Link to="/worlds" className={PLAY_LINK_CLASSES}>
              Play
            </Link>
          )}
          <button
            type="button"
            disabled
            className={CREATE_LINK_CLASSES}
            title="World authoring is coming soon"
          >
            Create a world
            <span className="text-xs uppercase tracking-wider text-lucid-accent/70 border border-lucid-accent/40 rounded px-2 py-0.5">
              soon
            </span>
          </button>
        </div>

        <section
          aria-label="Announcements"
          className="text-left rounded-lg border border-lucid-accent/30 bg-lucid-elevated/30 p-5"
        >
          <div
            role="tablist"
            aria-label="What's new or next"
            className="flex gap-2 mb-3"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "new"}
              onClick={() => setTab("new")}
              className={`${TAB_BASE} ${tab === "new" ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              What's new
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "next"}
              onClick={() => setTab("next")}
              className={`${TAB_BASE} ${tab === "next" ? TAB_ACTIVE : TAB_INACTIVE}`}
            >
              What's next
            </button>
          </div>

          <div
            role="tabpanel"
            className="font-narration text-lucid-text/85 space-y-4 text-sm leading-relaxed max-h-72 overflow-y-auto"
          >
            {tab === "new" ? (
              <>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    2026-06-05 — Modifiable environments
                  </h3>
                  <p>
                    Carve your initials into a tree, scratch an arrow on
                    a stone, scuff a footprint in mud — the room
                    remembers next time you visit, and the narrator
                    references the marks you left.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    2026-06-04 — Examined details stick around
                  </h3>
                  <p>
                    When you look closely at a thing, the details the
                    narrator gives you are remembered for the rest of
                    the session — so a key you spotted in a drawer is
                    still a key when you come back for it three turns
                    later.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    2026-06-03 — Bare "take" picks the obvious thing
                  </h3>
                  <p>
                    If there's only one thing in the room you could
                    plausibly grab, typing just "take" or "grab" now
                    picks it up instead of asking what you meant.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    2026-06-03 — Gibberish no longer moves you
                  </h3>
                  <p>
                    "xyzzy" and other unrecognizable input used to
                    occasionally get routed as a movement command and
                    drag you to a random exit. Nonsense now just bounces
                    back as nonsense, no turn burned.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    2026-06-02 — Failed actions don't burn the turn
                  </h3>
                  <p>
                    A rejected action — wrong passcode, locked door,
                    nothing to take — no longer advances the clock.
                    Retry without losing ground.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    2026-06-02 — Ambiguous targets ask which one
                  </h3>
                  <p>
                    "Take the bottle" with two bottles in the room now
                    pauses and asks you to pick, instead of guessing
                    wrong and consuming your turn.
                  </p>
                </article>
              </>
            ) : (
              <>
                <article>
                  <h3 className={ENTRY_HEADING}>Multi-hop turns</h3>
                  <p>
                    "Go to the kitchen and grab the knife" should walk
                    you there, pick the knife up, and narrate each beat
                    along the way — not silently teleport past the
                    intermediate room.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>
                    No more invented details on examine
                  </h3>
                  <p>
                    The narrator sometimes embellishes when you look
                    closely — a bookcase grows an iron key that isn't
                    actually there, then vanishes when you try to take
                    it. Clamping prose to real world state is next up.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>Smarter quick-action chips</h3>
                  <p>
                    The Move / Do / Say strip is getting smarter about
                    surfacing what's actually useful right now — exits
                    you haven't tried, characters who just spoke, items
                    you can carry — so a single tap covers more turns.
                  </p>
                </article>
                <article>
                  <h3 className={ENTRY_HEADING}>Stickier item descriptions</h3>
                  <p>
                    Building on examined-detail memory: once the
                    narrator describes a thing one way, that wording
                    should be the canonical reference next turn, so
                    items don't morph between sentences.
                  </p>
                </article>
              </>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
